import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Copy, MessageCircle, Phone, RefreshCw, CheckCircle, XCircle, Inbox, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/store';
import WhatsAppDeliveryLog from './WhatsAppDeliveryLog';
import WhatsAppRecipients from './WhatsAppRecipients';

const SECTIONS = [
  { id: 'overview', label: 'Bot setup' },
  { id: 'log', label: 'Delivery log' },
  { id: 'recipients', label: 'Recipients' },
];

/**
 * Admin → WhatsApp tab.
 * "Bot setup": DoubleTick configuration, the webhook URL to paste in the
 * DoubleTick dashboard, the staff whitelist and recent sessions.
 * "Delivery log": every reminder / digest attempt and what DoubleTick said.
 * "Recipients": every active employee, their WhatsApp number and a test send.
 */
export default function WhatsAppAdminPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [section, setSection] = useState('overview');
  const [recipients, setRecipients] = useState(null);
  const [recipientsLoading, setRecipientsLoading] = useState(false);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await api.getWhatsappStatus();
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load WhatsApp status');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecipients = useCallback(async () => {
    setRecipientsLoading(true);
    try {
      const res = await api.getWhatsappRecipients();
      setRecipients(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load recipients');
    } finally {
      setRecipientsLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, []);
  useEffect(() => {
    if (section !== 'overview' && !recipients) fetchRecipients();
  }, [section, recipients, fetchRecipients]);

  const sectionTabs = (
    <div className="flex flex-wrap gap-2" role="tablist" data-testid="whatsapp-sections">
      {SECTIONS.map((s) => (
        <button
          key={s.id}
          role="tab"
          aria-selected={section === s.id}
          onClick={() => setSection(s.id)}
          className={`h-9 rounded-full px-4 text-[12.5px] font-semibold border transition-colors ${section === s.id ? 'bg-heda-ink text-white border-heda-ink' : 'bg-white text-heda-ink border-heda-line hover:border-heda-gold'}`}
          data-testid={`whatsapp-section-${s.id}`}
        >
          {s.label}
        </button>
      ))}
    </div>
  );

  if (section === 'log') {
    return (
      <div className="space-y-4" data-testid="admin-whatsapp-panel">
        {sectionTabs}
        <WhatsAppDeliveryLog people={recipients?.recipients || []} />
      </div>
    );
  }
  if (section === 'recipients') {
    return (
      <div className="space-y-4" data-testid="admin-whatsapp-panel">
        {sectionTabs}
        <WhatsAppRecipients data={recipients} loading={recipientsLoading} onChanged={fetchRecipients} />
      </div>
    );
  }

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Webhook URL copied');
  };

  if (loading) {
    return <div className="flex items-center justify-center gap-2 text-sm text-heda-mute py-10"><Loader2 className="w-5 h-5 animate-spin text-heda-gold" /> Loading…</div>;
  }
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="admin-whatsapp-panel">
      {sectionTabs}
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <MessageCircle className="w-5 h-5 text-heda-gold" />
          <h2 className="font-display text-[17px] sm:text-[19px] font-bold text-heda-ink">WhatsApp Quotation Agent</h2>
          <Badge variant="outline" className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider shadow-none border-transparent bg-amber-50 text-amber-700">BETA</Badge>
        </div>
        <Button className="h-10 text-[12.5px] rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold" size="sm" variant="outline" onClick={fetchStatus} data-testid="whatsapp-refresh-btn">
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
        </Button>
      </div>

      {/* Configuration card */}
      <Card className="rounded-2xl border-heda-line bg-white shadow-none">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-2">
            {data.configured ? (
              <>
                <CheckCircle className="w-4 h-4 text-emerald-700" />
                <span className="text-sm font-semibold text-emerald-700">Configured</span>
              </>
            ) : (
              <>
                <XCircle className="w-4 h-4 text-red-700" />
                <span className="text-sm font-semibold text-red-700">Not configured — check backend .env</span>
              </>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div>
              <p className="heda-label">Business Number</p>
              <p className="font-mono text-heda-ink" data-testid="whatsapp-business-number">+{data.waba_number}</p>
            </div>
            <div>
              <p className="heda-label">DoubleTick Base URL</p>
              <p className="font-mono text-heda-ink text-xs break-all">{data.base_url}</p>
            </div>
          </div>

          {/* Webhook URL with copy */}
          {data.webhook_url && (
            <div className="mt-2 p-3 rounded-xl border border-heda-line bg-heda-cream/50">
              <p className="heda-label">Webhook URL — paste this in DoubleTick → Settings → Webhooks</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 min-w-0 text-xs font-mono bg-white border border-heda-line rounded-xl px-3 py-2 break-all" data-testid="whatsapp-webhook-url">
                  {data.webhook_url}
                </code>
                <Button className="h-10 w-10 p-0 rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold" size="sm" variant="outline" onClick={() => copyUrl(data.webhook_url)} data-testid="whatsapp-copy-webhook-btn">
                  <Copy className="w-3.5 h-3.5" />
                </Button>
              </div>
              <p className="text-[11px] text-heda-mute mt-1">
                Subscribe to <span className="font-semibold">Message Received</span> event in DoubleTick. Bot only responds to whitelisted senders below.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Allowed senders with per-user toggle */}
      <Card className="rounded-2xl border-heda-line bg-white shadow-none">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Phone className="w-4 h-4 text-heda-gold" />
            <h3 className="font-display text-[15px] font-bold text-heda-ink">
              Allowed Senders ({(data.whitelisted_users || []).filter(u => u.whatsapp_enabled).length} active / {data.whitelisted_users?.length || 0})
            </h3>
          </div>
          <p className="text-[13px] text-heda-sub mb-3">
            Toggle <span className="font-semibold">WhatsApp access</span> for each staff/admin user below. Only users
            with the toggle <span className="text-emerald-700 font-semibold">ON</span> can use the bot. Users without a phone
            number set don't appear here — edit them in the <span className="font-semibold">Staff</span> tab first.
          </p>
          {data.whitelisted_users?.length === 0 ? (
            <p className="text-sm text-heda-mute py-6 text-center">No users with phone numbers configured yet. Add a phone number in the Staff tab.</p>
          ) : (
            <div className="border border-heda-line rounded-xl overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="heda-thead">
                  <tr>
                    <th className="px-3 sm:px-4 py-2.5 font-semibold text-left">Name</th>
                    <th className="px-3 sm:px-4 py-2.5 font-semibold text-left hidden md:table-cell">Phone</th>
                    <th className="px-3 sm:px-4 py-2.5 font-semibold text-left hidden sm:table-cell">Role</th>
                    <th className="px-2 sm:px-4 py-2.5 font-semibold text-center">Bot Access</th>
                    <th className="px-2 sm:px-4 py-2.5 font-semibold text-center">Walk-in</th>
                    <th className="px-2 sm:px-4 py-2.5 font-semibold text-center">Quotation</th>
                  </tr>
                </thead>
                <tbody>
                  {data.whitelisted_users.map((u, i) => (
                    <tr key={u.id || i} className="border-t border-heda-line">
                      <td className="px-3 sm:px-4 py-3 font-medium text-heda-ink">
                        {u.name}
                        <div className="text-[11px] font-normal text-heda-mute tabular-nums md:hidden">{u.phone || '—'}</div>
                      </td>
                      <td className="px-3 sm:px-4 py-3 tabular-nums text-heda-sub hidden md:table-cell">{u.phone || '—'}</td>
                      <td className="px-3 sm:px-4 py-3 hidden sm:table-cell">
                        <Badge variant="outline" className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider shadow-none border-transparent bg-heda-cream text-heda-mute">{u.role}</Badge>
                      </td>
                      <td className="px-2 sm:px-4 py-3 text-center">
                        <button
                          onClick={async () => {
                            try {
                              await api.toggleWhatsappUser(u.id, !u.whatsapp_enabled);
                              toast.success(`${u.name}: Bot ${!u.whatsapp_enabled ? 'enabled' : 'disabled'}`);
                              fetchStatus();
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Failed to toggle');
                            }
                          }}
                          className={`min-h-0 relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${u.whatsapp_enabled ? 'bg-heda-ink' : 'bg-heda-line'}`}
                          data-testid={`whatsapp-toggle-${u.id}`}
                          title={u.whatsapp_enabled ? 'Click to disable' : 'Click to enable'}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${u.whatsapp_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                      </td>
                      <td className="px-2 sm:px-4 py-3 text-center">
                        <button
                          disabled={!u.whatsapp_enabled}
                          onClick={async () => {
                            try {
                              await api.toggleWhatsappFeature(u.id, 'walkin', !u.whatsapp_walkin_enabled);
                              toast.success(`${u.name}: Walk-in ${!u.whatsapp_walkin_enabled ? 'ON' : 'OFF'}`);
                              fetchStatus();
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Failed to toggle');
                            }
                          }}
                          className={`min-h-0 relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${u.whatsapp_walkin_enabled ? 'bg-heda-ink' : 'bg-heda-line'}`}
                          data-testid={`walkin-toggle-${u.id}`}
                          title={!u.whatsapp_enabled ? 'Enable Bot Access first' : (u.whatsapp_walkin_enabled ? 'Disable walk-in flow' : 'Enable walk-in flow')}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${u.whatsapp_walkin_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                      </td>
                      <td className="px-2 sm:px-4 py-3 text-center">
                        <button
                          disabled={!u.whatsapp_enabled}
                          onClick={async () => {
                            try {
                              await api.toggleWhatsappFeature(u.id, 'quotation', !u.whatsapp_quotation_enabled);
                              toast.success(`${u.name}: Quotation ${!u.whatsapp_quotation_enabled ? 'ON' : 'OFF'}`);
                              fetchStatus();
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Failed to toggle');
                            }
                          }}
                          className={`min-h-0 relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${u.whatsapp_quotation_enabled ? 'bg-heda-ink' : 'bg-heda-line'}`}
                          data-testid={`quotation-toggle-${u.id}`}
                          title={!u.whatsapp_enabled ? 'Enable Bot Access first' : (u.whatsapp_quotation_enabled ? 'Disable quotation flow' : 'Enable quotation flow')}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${u.whatsapp_quotation_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-[11px] text-heda-mute px-4 py-2.5 bg-heda-cream/50 border-t border-heda-line">
                <span className="font-semibold">Walk-in</span> — salesperson sends voice/text to log a walk-in customer.
                {' '}<span className="font-semibold">Quotation</span> — salesperson sends voice/text and gets a PDF quotation back.
                {' '}Quotation flow is in trial mode — keep it OFF for staff until UAT passes.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent sessions */}
      <Card className="rounded-2xl border-heda-line bg-white shadow-none">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Inbox className="w-4 h-4 text-heda-gold" />
            <h3 className="font-display text-[15px] font-bold text-heda-ink">Recent Sessions</h3>
          </div>
          {(!data.recent_sessions || data.recent_sessions.length === 0) ? (
            <p className="text-sm text-heda-mute py-6 text-center">No WhatsApp conversations yet.</p>
          ) : (
            <div className="space-y-2">
              {data.recent_sessions.map((s, i) => (
                <div key={i} className="border border-heda-line rounded-xl px-3 py-2.5 text-xs flex items-center justify-between gap-2">
                  <div>
                    <p className="font-mono font-semibold">+{s.phone}</p>
                    <p className="text-heda-sub">State: <span className="font-semibold">{s.state}</span></p>
                  </div>
                  <p className="text-heda-mute">{new Date(s.updated_at).toLocaleString()}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Setup instructions */}
      <Card className="rounded-2xl border-heda-line bg-white shadow-none">
        <CardContent className="p-5 space-y-2 text-[13px]">
          <p className="font-display text-[15px] font-bold text-heda-ink">Setup Instructions</p>
          <ol className="list-decimal list-inside space-y-1 text-heda-sub">
            <li>Log into <a href="https://web.doubletick.io" target="_blank" rel="noreferrer" className="text-heda-gold underline">DoubleTick dashboard</a> → Settings → Webhooks.</li>
            <li>Click <span className="font-semibold">+ New Webhook</span>, paste the URL above, and subscribe to <span className="font-semibold">Message Received</span>.</li>
            <li>On the Staff tab, set a WhatsApp phone number for each user who should be allowed to use the bot.</li>
            <li>Staff sends a message like <span className="font-mono bg-heda-cream px-1 rounded-lg">"Name: Rohit Sharma, 9876543210, Century 18MM 5 sheets, Sainik 6MM 3 sheets"</span> — bot replies with summary + Confirm button → on Confirm, sends back a PDF quotation.</li>
            <li>Voice notes work too (any language).</li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
}
